/**
 * Luxury gift packaging drawer (PDP, cart drawer, etc.).
 * Posts to /cart/add.js and updates ThatWorks.cartElement — no dependency on main-product-tw-js.
 */

const TAG = 'luxury-gift-drawer';

class LuxuryGiftDrawer extends HTMLElement {
  static #documentGiftUiBound = false;

  /** Skip one `addedToCart` after our own add — cart DOM isn’t updated yet so gift detection misfires. */
  static #suppressAddedToCartOnce = false;

  #selectedVariantIds = [];
  #isSubmitting = false;

  connectedCallback() {
    if (this.#isInitialized()) return;

    this.dataset.luxuryGiftDrawerInit = 'true';

    this.#bindDocumentListeners();
    this.#bindDocumentGiftUi();
    this.#bindDrawerInteractions();
    this.#updateState();
  }

  #isInitialized() {
    return this.dataset.luxuryGiftDrawerInit === 'true';
  }

  #bindDocumentListeners() {
    document.body.addEventListener('addedToCart', this.#onAddedToCart);
  }

  /**
   * PDP `.luxury_gift_wrapping` + cart line `.cart-item-gift-add` (delegated; one listener).
   */
  #bindDocumentGiftUi() {
    if (LuxuryGiftDrawer.#documentGiftUiBound) return;

    LuxuryGiftDrawer.#documentGiftUiBound = true;
    document.addEventListener('click', LuxuryGiftDrawer.#onDocumentGiftUiClick);
  }

  static #onDocumentGiftUiClick = (event) => {
    const qtyReveal = event.target.closest('.cart-item-gift__qty-reveal');

    if (qtyReveal) {
      const root = qtyReveal.closest('.cart-item-gift');

      if (!root) return;

      root.classList.add('cart-item-gift--qty-editing');
      qtyReveal.setAttribute('aria-expanded', 'true');

      const input = root.querySelector('.cart-item-gift__qty-editor .quantity__input');

      input?.focus();

      return;
    }

    const giftBtn = event.target.closest('.cart-item-gift-add');

    if (giftBtn) {
      const drawer = document.querySelector('luxury-gift-drawer');

      if (!drawer) return;

      const lineKey = giftBtn.dataset.lineKey?.trim();
      const parentVariantId = giftBtn.dataset.parentVariantId?.trim();

      if (lineKey) {
        drawer.dataset.lineId = lineKey;
      } else {
        delete drawer.dataset.lineId;
      }

      if (parentVariantId) {
        drawer.dataset.variantId = parentVariantId;

        const atc = drawer.querySelector('.atc_btn');

        if (atc) {
          atc.dataset.id = parentVariantId;
        }
      }

      drawer.classList.add('drawer-open');
      window.ThatWorks?.cartElement?.close?.();

      return;
    }

    const trigger = event.target.closest('.luxury_gift_wrapping');

    if (!trigger) return;

    document.querySelector('luxury-gift-drawer')?.classList.add('drawer-open');
  };

  #onAddedToCart = () => {
    if (LuxuryGiftDrawer.#suppressAddedToCartOnce) {
      LuxuryGiftDrawer.#suppressAddedToCartOnce = false;
    }
  };

  #bindDrawerInteractions() {
    this.addEventListener('click', this.#onDrawerClick);
    this.addEventListener('change', this.#onQtyChange);
  }

  #closeDrawer(decision = 'skipped') {
    this.classList.remove('drawer-open');
    document.body.dispatchEvent(
      new CustomEvent('luxuryGiftDecision', {
        bubbles: true,
        detail: {
          decision,
        },
      })
    );
  }

  #onDrawerClick = (event) => {
    const target = event.target;

    // Overlay (host / backdrop) — not the sliding panel
    if (!target.closest('.luxury_gift_drawer_holder')) {
      this.#closeDrawer('skipped');

      return;
    }

    if (this.#isCloseFromHeader(target)) {
      this.#closeDrawer('skipped');

      return;
    }

    const dec = target.closest('[data-decrement]');

    if (dec) {
      this.#adjustQty(dec.getAttribute('data-decrement'), -1);

      return;
    }

    const inc = target.closest('[data-increment]');

    if (inc) {
      this.#adjustQty(inc.getAttribute('data-increment'), 1);

      return;
    }

    const moreInfo = target.closest('.more_info');

    if (moreInfo) {
      const productId = moreInfo.dataset.id;

      this.querySelector('.body')?.classList.remove('active');
      this.querySelector(`#luxury_${productId}`)?.classList.add('active');
      this.querySelector('.header')?.classList.remove('active');
      this.querySelector('.header_luxury_product')?.classList.add('active');

      return;
    }

    const backHeader = target.closest('.header_luxury_product');

    if (backHeader) {
      this.querySelector('.body')?.classList.add('active');
      this.querySelectorAll('.luxury_product_show').forEach((el) => el.classList.remove('active'));
      this.querySelector('.header')?.classList.add('active');
      this.querySelector('.header_luxury_product')?.classList.remove('active');

      return;
    }

    const atc = target.closest('.atc_btn');

    if (atc) {
      this.#submitAddToCart();

      return;
    }
  };

  #isCloseFromHeader(target) {
    const svg = target.closest('svg');

    if (!svg) return false;

    return Boolean(svg.closest('.header'));
  }

  #adjustQty(selector, delta) {
    if (!selector) return;

    const input = this.querySelector(selector);

    if (!input) return;

    const current = parseInt(input.value, 10) || 0;

    if (delta < 0 && current <= 0) return;

    input.value = String(Math.max(0, current + delta));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onQtyChange = (event) => {
    const input = event.target;

    if (!input.classList?.contains('qty_input')) return;

    this.#updateState();
  };

  /**
   * Links each gift line to a parent cart row: `_connection` = parent line `key`, or `pv:<variantId>` before parent exists in cart.
   */
  #buildGiftConnectionProperties() {
    const parentLineKey = this.dataset.lineId?.trim();
    const parentVariantId = this.dataset.variantId?.trim();
    const properties = {};

    if (parentLineKey) {
      properties._connection = parentLineKey;
    } else if (parentVariantId) {
      properties._connection = `pv:${parentVariantId}`;
    }

    if (parentVariantId) {
      properties._parent_variant_id = parentVariantId;
    }

    return properties;
  }

  #updateState() {
    const allQtys = this.querySelectorAll('.qty_input');

    for (const input of allQtys) {
      const qty = parseInt(input.value, 10) || 0;
      const variantId = input.dataset.id;

      if (!variantId) continue;

      const idStr = String(variantId);

      if (qty > 0 && !this.#selectedVariantIds.includes(idStr)) {
        this.#selectedVariantIds.push(idStr);
      }

      if (qty === 0) {
        this.#selectedVariantIds = this.#selectedVariantIds.filter((id) => id !== idStr);
      }
    }

    const hasAny = [...allQtys].some((q) => parseInt(q.value, 10) > 0);
    const atcBtn = this.querySelector('.atc_btn');

    if (!atcBtn) return;

    atcBtn.classList.toggle('disabled', !hasAny);
  }

  #submitAddToCart() {
    void this.#submitAddToCartAsync();
  }

  async #submitAddToCartAsync() {
    if (this.#isSubmitting) return;
    const baseProps = this.#buildGiftConnectionProperties();

    if (!baseProps._connection) {
      console.warn('luxury-gift-drawer: missing parent link — set data-line-id and/or data-variant-id on <luxury-gift-drawer>');

      return;
    }

    const parentVariantId = this.dataset.variantId?.trim();
    const parentLineKey = this.dataset.lineId?.trim();

    const giftItems = [];

    for (const input of this.querySelectorAll('.qty_input')) {
      const qty = parseInt(input.value, 10) || 0;

      if (qty <= 0) continue;

      const id = input.dataset.id;

      if (!id) continue;

      giftItems.push({
        id: Number(id),
        quantity: qty,
        properties: { ...baseProps },
      });
    }

    if (giftItems.length === 0) return;

    this.#isSubmitting = true;
    const atc = this.querySelector('.atc_btn');
    atc?.setAttribute('aria-busy', 'true');
    this.querySelector('[data-gift-error]')?.remove();
    const items = [...giftItems];

    const isGiftLine = (row) => Boolean(row.properties && row.properties._connection);

    try {
      const res = await fetch(`${window.ThatWorks?.shopSettings?.routes?.cart_url || '/cart'}.js`);

      if (!res.ok) throw new Error('Unable to verify the cart. Please try again.');

      const cart = await res.json();
      const cartItems = cart.items || [];
      const parents = cartItems.filter((row) => parentLineKey
        ? row.key === parentLineKey
        : !isGiftLine(row) && String(row.variant_id) === String(parentVariantId));

      // Discount applications can change a line key. Never recreate a cart
      // line by variant ID: this would lose the app's gift properties.
      if ((parentLineKey && parents.length === 0) || parents.length > 1) {
        throw new Error('Your cart has changed. Please reopen gift wrapping from the updated cart.');
      }

      if (parents.length === 1) {
        giftItems.forEach((item) => { item.properties._connection = parents[0].key; });
      } else if (!parentLineKey && parentVariantId) {
        items.unshift({ id: Number(parentVariantId), quantity: 1 });
      }

      LuxuryGiftDrawer.#suppressAddedToCartOnce = true;

      await LuxuryGiftDrawer.#postCartAddItems(items);
      this.#closeDrawer('added');
    } catch (e) {
      LuxuryGiftDrawer.#suppressAddedToCartOnce = false;
      console.error(e);
      let error = this.querySelector('[data-gift-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-gift-error', '');
        error.setAttribute('role', 'alert');
        atc?.insertAdjacentElement('beforebegin', error);
      }
      error.textContent = e.message;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'luxury-gift-drawer' });
    } finally {
      this.#isSubmitting = false;
      atc?.removeAttribute('aria-busy');
    }
  }

  static #cartAddJsUrl() {
    const raw = window.ThatWorks?.shopSettings?.routes?.cart_add_url || '/cart/add';

    if (raw.endsWith('.js')) return raw;

    return `${raw.replace(/\/$/, '')}.js`;
  }

  /** Cart template: `#main-cart-items` exists; drawer markup does not. */
  static #isCartPage() {
    return Boolean(document.getElementById('main-cart-items'));
  }

  /**
   * Same section ids as `CartItems.getSectionsToRender()` in cart.js (cart page).
   * Response keys match `data.sections[ apiId ]` from /cart/add.js.
   */
  static #getCartPageSectionsConfig() {
    const mainItems = document.getElementById('main-cart-items');

    if (!mainItems?.dataset?.id) return null;

    const sections = [
      {
        apiId: mainItems.dataset.id,
        targetId: 'main-cart-items',
        selector: '.js-contents',
      },
      {
        apiId: 'cart-icon-bubble',
        targetId: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        apiId: 'cart-live-region-text',
        targetId: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];

    const footer = document.getElementById('main-cart-footer');

    if (footer?.dataset?.id) {
      sections.push({
        apiId: footer.dataset.id,
        targetId: 'main-cart-footer',
        selector: '.js-contents',
      });
    }

    return sections;
  }

  static #parseSectionInnerHTML(html, selector) {
    if (!html) return '';

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);

    return el ? el.innerHTML : '';
  }

  /** Updates main cart DOM from /cart/add.js `sections` — does not open the cart drawer. */
  static #applyCartPageSectionsFromAddResponse(data, config) {
    const mainItems = document.getElementById('main-cart-items');
    const cartItemsEl = mainItems?.closest('cart-items');

    if (cartItemsEl && typeof data.item_count === 'number') {
      cartItemsEl.classList.toggle('is-empty', data.item_count === 0);
    }

    const cartDrawerWrapper = document.querySelector('cart-drawer');

    if (cartDrawerWrapper && typeof data.item_count === 'number') {
      cartDrawerWrapper.classList.toggle('is-empty', data.item_count === 0);
    }

    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter && typeof data.item_count === 'number') {
      cartFooter.classList.toggle('is-empty', data.item_count === 0);
    }

    if (!data.sections) return;

    for (const section of config) {
      const html = data.sections[section.apiId];

      if (!html) continue;

      const target = document.getElementById(section.targetId);

      if (!target) continue;

      const elementToReplace = target.querySelector(section.selector) || target;
      const inner = LuxuryGiftDrawer.#parseSectionInnerHTML(html, section.selector);

      if (inner !== '') {
        elementToReplace.innerHTML = inner;
      }
    }
  }

  /** Mirrors main-product-tw-js add_items_to_cart: sections + renderContents + addedToCart. */
  static async #postCartAddItems(items) {
    const cartEl = window.ThatWorks?.cartElement;
    const cartPageConfig = LuxuryGiftDrawer.#isCartPage() ? LuxuryGiftDrawer.#getCartPageSectionsConfig() : null;
    const useCartPageApply = Boolean(cartPageConfig);

    let sectionsParam;

    if (useCartPageApply) {
      sectionsParam = cartPageConfig.map((s) => s.apiId).join(',');
    } else {
      if (!cartEl || typeof cartEl.getSectionsToRender !== 'function') {
        throw new Error('luxury-gift-drawer: ThatWorks.cartElement missing');
      }

      sectionsParam = cartEl.getSectionsToRender().map((s) => s.id).join(',');
    }

    const url = LuxuryGiftDrawer.#cartAddJsUrl();

    const body = {
      items,
      sections: sectionsParam,
    };

    if (useCartPageApply) {
      body.sections_url = window.location.pathname;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      let errText = '';

      try {
        errText = await res.text();
      } catch {
        // ignore
      }

      throw new Error(errText || `${res.status} ${res.statusText}`);
    }

    const data = await res.json();

    window.ThatWorks?.engraveDrawer?.close?.();

    if (useCartPageApply) {
      LuxuryGiftDrawer.#applyCartPageSectionsFromAddResponse(data, cartPageConfig);
    } else if (cartEl && typeof cartEl.renderContents === 'function') {
      cartEl.renderContents(data);
    }

    if (cartEl) {
      cartEl.classList.remove('is-empty');
    }

    document.body.dispatchEvent(
      new CustomEvent('addedToCart', {
        bubbles: true,
        detail: {
          source: 'luxury-gift-drawer',
        },
      })
    );

    return data;
  }

  disconnectedCallback() {
    document.body.removeEventListener('addedToCart', this.#onAddedToCart);
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, LuxuryGiftDrawer);
}
